import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * VRG-511 — the signed-in viewer's own consent to being named on ONE certificate's
 * public provenance.
 *
 * ## This hook answers TWO questions with one request, and that is deliberate
 *
 * The endpoint 404s when the caller has never held the item and 200s with their
 * stored preference when they have. So a successful response means both:
 *
 *   1. **you appear on this item's chain** — which, for a viewer who is not the
 *      current owner, is exactly "you previously owned this"; and
 *   2. **this is your current answer**, which the toggle needs anyway.
 *
 * Deriving (1) from the provenance list instead would not work, and the reason is
 * the feature itself: a past owner reads that list through the PUBLIC endpoint,
 * where their own hop is anonymised the moment they opt out. They would disappear
 * from their own evidence. The 404/200 split asks the server the question directly
 * and is unaffected by what the chain publishes.
 *
 * ## Why the retry policy is a PREDICATE and not `false`
 *
 * A 404 is the ORDINARY answer for most viewers — every stranger who taps a tag
 * gets one. React Query's default retries a failed query three times with backoff,
 * so retrying it would fire four requests to be told the same thing and leave the
 * card seconds away from settling into not appearing. That is what first argued for
 * `retry: false`.
 *
 * But `false` also refuses to retry a 401, a 403 or a 5xx, and those are exactly the
 * failures worth retrying — a token still being minted during bootstrap, a request
 * that raced the dev session, a restarting API. Because a rejected query leaves
 * `data` undefined and the card gates on `data`, ANY such blip would hide the card
 * permanently for the life of the page, with no route back to it: the user sees the
 * stranger's view of an item they used to own and nothing tells them why.
 *
 * So: never retry a 404 (it is an answer, not a failure), retry anything else twice.
 *
 * Honest note on provenance: this was written defensively, not to fix an observed
 * bug. The 403s seen while verifying this ticket came from a hand-built `fetch`
 * carrying the wrong token, and the app's own request returned 200 throughout. The
 * asymmetry is still worth encoding, because the cost of being wrong is one-way.
 */

export type ProvenanceConsent = { shares_identity: boolean }

export const provenanceConsentKey = (certificateId: string) => [
  'provenance-consent',
  certificateId,
]

/**
 * `null` = a 404, i.e. the caller has no hop on this certificate.
 *
 * Mapped to a resolved `null` rather than left as a thrown error so callers can say
 * `data === null` for "not a past owner" and reserve `isError` for a real failure. A
 * network blip and "you never owned this" must not look the same to the UI: the
 * first should not silently hide a control the user is entitled to.
 */
const getProvenanceConsent = async (
  certificateId: string,
): Promise<ProvenanceConsent | null> => {
  try {
    const response = await createApiCall({
      url: `/certificates/customer/provenance-consent/${certificateId}`,
      throwError: true,
    })
    return response.data as ProvenanceConsent
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status === 404) return null
    throw error
  }
}

export const useProvenanceConsent = (certificateId?: string, enabled = true) =>
  useQuery({
    queryKey: provenanceConsentKey(certificateId as string),
    queryFn: () => getProvenanceConsent(certificateId as string),
    enabled: Boolean(certificateId) && enabled,
    /*
     * See the docblock. A 404 never reaches here — `getProvenanceConsent` resolves it
     * to `null` — so this predicate governs real failures only, and the `=== 404`
     * arm is belt-and-braces in case that mapping is ever removed.
     */
    retry: (failureCount, error) => {
      const status = (error as {response?: {status?: number}})?.response?.status
      if (status === 404) return false
      return failureCount < 2
    },
  })

export const useUpdateProvenanceConsent = (certificateId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (sharesIdentity: boolean) => {
      const response = await createApiCall({
        method: 'PATCH',
        url: `/certificates/customer/provenance-consent/${certificateId}`,
        data: { shares_identity: sharesIdentity },
        throwError: true,
      })
      return response.data as ProvenanceConsent
    },
    onSuccess: data => {
      // Write through rather than invalidate: the server echoes the stored value, so
      // there is nothing to re-fetch, and an invalidate would flip the toggle back to
      // its old position for one paint while the refetch is in flight.
      queryClient.setQueryData(provenanceConsentKey(certificateId), data)
      // The public chain is what actually changed, and it is a DIFFERENT query. Miss
      // this and the person toggles their name off, looks at the history section
      // directly below, and still sees themselves — concluding it did not work.
      queryClient.invalidateQueries({ queryKey: ['provenance', certificateId] })
      queryClient.invalidateQueries({ queryKey: ['certificate-public', certificateId] })
    },
  })
}
